<template>
  <!-- only load if rxDB has been synced -->
  <div
    class="px-safe py-safe"
    v-if="isAllCollectionsAdded && isWaitOver"
  >
    <Toast />
    <ConfirmDialog></ConfirmDialog>
    <div v-if="isVisible && (!isHideHeader || Helper.isMobileSize())">
      <IndexLayout />
    </div>
    <RouterView />
  </div>
  <div v-else>
    <div
      class="absolute inset-0 border rounded flex items-center justify-center"
    >
      <div class="flex flex-col items-center">
        <div class="flex items-center space-x-4">
          <i class="pi pi-spin pi-spinner text-3xl text-black"></i>
          <div class="text-black">Just a moment, doing some magic ! ✨</div>
        </div>
        <!-- current version to the screen -->
        <div class="text-black text-sm mt-2">
          v{{ Helper.getCurrentVersion() }}
        </div>
        <div class="text-black text-sm mt-2">
          {{ `Navigating in ${counter} seconds` }}
        </div>
        <!-- sync flow status and user signed in -->
        <div class="text-black text-sm mt-2">
          <div class="flex items-center gap-2 mb-2">
            <span>First Sync Completed:</span>
            <i
              :class="[
                'pi text-xl',
                isFirstSyncCompleted
                  ? 'pi-check-circle text-green-500'
                  : 'pi-times-circle text-red-500'
              ]"
            ></i>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <span>Table Added:</span>
            <i
              :class="[
                'pi text-xl',
                isCollectionTableAdded
                  ? 'pi-check-circle text-green-500'
                  : 'pi-times-circle text-red-500'
              ]"
            ></i>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <span>Table Synced:</span>
            <i
              :class="[
                'pi text-xl',
                isAllCollectionsSynced
                  ? 'pi-check-circle text-green-500'
                  : 'pi-times-circle text-red-500'
              ]"
            ></i>
          </div>

          <div class="flex items-center gap-2">
            <span>User Signed In:</span>
            <i
              :class="[
                'pi text-xl',
                isUserSignedIn
                  ? 'pi-check-circle text-green-500'
                  : 'pi-times-circle text-red-500'
              ]"
            ></i>
          </div>
        </div>
        <div class="text-black text-sm mt-2">
          <div class="flex flex-col items-center">
            Total Databases ({{ indexedDBTables.length }})
          </div>
          <div v-if="indexedDBTables.length > 0">
            <div
              v-for="(db, index) in indexedDBTables"
              :key="index"
            >
              <li>{{ db.database }} ({{ db.tables.length }} tables)</li>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <NewUpdateDialog
    :visible="updateObj.isVisible"
    :message="updateObj.errorMsg"
    :onConfirm="onUpdateDialogClose"
    @update:visible="updateObj.isVisible = $event"
    :headerMsg="updateObj.headerMsg"
    :forceUpdate="updateObj.forceUpdate"
  />
  <EnvironmentInfoDialog
    :visible="envInfoObj.isVisible"
    :message="envInfoObj.message"
    :onConfirm="onEnvironmentInfoDialogClose"
    @update:visible="envInfoObj.isVisible = $event"
    :headerMsg="envInfoObj.headerMsg"
  />
  <OfflineDataSyncProgressDialog
    :visible="offlineDataProgressObj.isVisible"
    :error_message="offlineDataProgressObj.errorMsg"
    @update:visible="offlineDataProgressObj.isVisible = $event"
    :headerMsg="offlineDataProgressObj.headerMsg"
    :isLoading="offlineDataProgressObj.isLoading"
  />
  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    @update:visible="errorObj.isVisible = $event"
    :onConfirm="() => {}"
    :headerMsg="errorObj.headerMsg"
  />
  <DynamicDialog></DynamicDialog>
</template>

<script>
  import { defineComponent, ref, watch, onMounted, computed } from "vue";
  import NewUpdateDialog from "@/components/NewUpdateDialog.vue";
  import EnvironmentInfoDialog from "@/components/EnvironmentInfoDialog.vue";
  import OfflineDataSyncProgressDialog from "./components/OfflineDataSyncProgressDialog.vue";
  import IndexLayout from "src/layout/IndexLayout.vue";
  import { useRoute, useRouter } from "vue-router";
  import { App as capApp } from "@capacitor/app";
  import { StatusBar, Style } from "@capacitor/status-bar";
  import { useUserStore } from "@/stores/userStore";
  import { useAppSettingsStore } from "@/stores/appSettingsStore";
  import Auth from "@/utils/auth";
  import Helper from "./utils/helper";
  import Constants from "@/utils/constants";
  import { initDatabase, getCollection } from "@/plugins/rxdb/database";
  import { useRxDBStore } from "@/stores/rxDBStore";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import serviceWorkerManager from "@/utils/serviceWorkerUpdate";
  import updateEventBus from "@/utils/updateEventBus";
  import Exception from "src/utils/exception";
  import { useProfileStore } from "@/stores/profileStore";
  import { usePermissionsStore } from "@/stores/permissionsStore";
  import { checkRouteAccess } from "@/router";

  export default defineComponent({
    name: "App",
    components: {
      IndexLayout,
      NewUpdateDialog,
      EnvironmentInfoDialog,
      OfflineDataSyncProgressDialog,
      ErrorDialog
    },
    setup() {
      // Safety check for service worker manager
      if (!serviceWorkerManager) {
        console.warn("Service worker manager not available");
      }

      const didSync = ref(false);
      const settingsStore = ref(false);
      const errorObj = ref({
        isVisible: false,
        errorMsg: "",
        headerMsg: "Oops Error Occured !!"
      });
      const profileStore = useProfileStore();

      // Bridge global store-driven errors into the existing App ErrorDialog
      watch(
        () => profileStore.appErrorDialog.isVisible,
        (isVisible) => {
          if (isVisible) {
            errorObj.value.isVisible = true;
            errorObj.value.headerMsg =
              profileStore.appErrorDialog.headerMsg || "Oops Error Occured !!";
            errorObj.value.errorMsg =
              profileStore.appErrorDialog.errorMsg || "";
          }
        }
      );

      watch(
        () => errorObj.value.isVisible,
        (isVisible) => {
          if (!isVisible) {
            profileStore.hideAppErrorDialog();
          }
        }
      );
      const envInfoObj = ref({
        isVisible: false,
        headerMsg: "",
        message: ""
      });
      // rxDB Store
      const rxDBStore = useRxDBStore();
      const isAllCollectionsSynced = computed(
        () => rxDBStore.isAllCollectionsSynced
      );
      const counter = ref(3);
      const isWaitOver = ref(false);
      const interval = setInterval(() => {
        counter.value--;
        if (counter.value <= 0) {
          clearInterval(interval);
          isWaitOver.value = true;
          // listen for service worker update events
          listenForServiceWorkerUpdateEvents();
        }
      }, 1000);
      const isAllCollectionsAdded = computed(() => {
        return (
          (rxDBStore.isAllCollectionsAdded && rxDBStore.isFirstSyncCompleted) ||
          !userStore.isUserSignedIn()
        );
      });

      const isFirstSyncCompleted = computed(() => {
        return rxDBStore.isFirstSyncCompleted;
      });

      const isCollectionTableAdded = computed(() => {
        return rxDBStore.isAllCollectionsAdded;
      });
      const isUserSignedIn = computed(() => {
        return userStore.isUserSignedIn();
      });

      watch(
        isAllCollectionsSynced,
        (isSynced) => {
          if (isSynced) {
            didSync.value = true;
            // show completion and close dialog
            offlineDataProgressObj.value.headerMsg = "Sync complete";
            offlineDataProgressObj.value.isLoading = false;

            // Reset dialog after showing completion
            offlineDataProgressObj.value.isVisible = false;
            offlineDataProgressObj.value.errorMsg = "";
            offlineDataProgressObj.value.headerMsg = "";
            checkVersion();
          }
        },
        { deep: true }
      );

      const checkVersion = async () => {
        try {
          await getCollection(
            Constants.enums.TABLES_NAME_ENUM.APP_SETTINGS_TABLE
          );
        } catch (error) {
          Exception.catchException(error);
          return;
        }
        if (!settingsStore.value) {
          await useAppSettingsStore().fetchAppSettings();
          settingsStore.value = true;
        }
        let _forceUpdate = useAppSettingsStore().getAppSettings(
          Constants.enums.APP_SETTINGS.FORCE_UPDATE_VERSION
        );
        let _remoteVersion = useAppSettingsStore().getAppSettings(
          Constants.enums.APP_SETTINGS.CURRENT_WEB_VERSION
        );
        let _version = Helper.compareVersions(
          _remoteVersion,
          Helper.getCurrentVersion()
        );
        updateObj.value.forceUpdate = _forceUpdate == "true" ? true : false;
        if (_version > 0 && _forceUpdate == "true") {
          updateObj.value.isVisible = true;
          updateObj.value.headerMsg = "New Version Detected";
          updateObj.value.errorMsg = `<div><h2>⚠️ Update Required<br/><br/></h2><p>A new version of the app (<strong>v<span id='version-number'>${_remoteVersion}</span></strong>) is available, and this update is required to continue using the app.<br/></p><p>Please update now to access the latest features, improvements, and security fixes.</p></div>`;
        } else if (_version > 0 && _forceUpdate == "false") {
          updateObj.value.isVisible = true;
          updateObj.value.headerMsg = "New Update Available";
          updateObj.value.errorMsg = `<div><h2>⚠️ Update Available<br/><br/></h2><p>A new version of the app (<strong>v<span id='version-number'>${_remoteVersion}</span></strong>) is available!, Get the latest features, improvements, and fixes by updating now. You can continue using the current version, but we recommend updating for the best experience.</p></div>`;
        } else {
          if (didSync.value) {
            didSync.value = false;
          }
        }
      };

      if (Helper.isNativePlatForm()) {
        StatusBar.setOverlaysWebView({ overlay: false }); // Display content under transparent status bar (Android only)
        StatusBar.setStyle({ style: Style.Light }); // Adjust style (DARK or LIGHT)
      }
      const updateObj = ref({
        isVisible: false,
        errorMsg: "",
        headerMsg: "Oops Error Occured !!",
        forceUpdate: false
      });
      const offlineDataProgressObj = ref({
        isVisible: false,
        errorMsg: "",
        headerMsg: "Oops Error Occured !!",
        isLoading: false
      });
      const route = useRoute();
      const router = useRouter();
      const permissionsStore = usePermissionsStore();
      const isVisible = ref(true);
      const isHideHeader = computed(() => {
        return (
          // logout
          route.path.includes(Constants.enums.ROUTE_ENUM.LOG_OUT) ||
          route.path.includes(Constants.enums.ROUTE_ENUM.LOG_IN) ||
          // auth callback routes
          route.path.includes(Constants.enums.ROUTE_ENUM.AUTH_CALLBAK) ||
          route.path.includes(
            Constants.enums.ROUTE_ENUM.AUTH_CALLBAK_MOBILE_APP
          )
        );
      });

      capApp.addListener("appUrlOpen", function (data) {
        let routUrl = data.url.split(":/")[1];
        router.push(routUrl);
      });
      const userStore = useUserStore();
      const listenForAuthChange = () => {
        Auth.onAuthStateChange((event) => {
          // console.log("Auth STATE CHANGE TO :", event, session);

          switch (event) {
            case "SIGNED_OUT":
              userStore.setIsUserSignedIn(false);
              break;
            case "SIGNED_IN":
            case "INITIAL_SESSION":
              userStore.setIsUserSignedIn(true);
              break;
            case "TOKEN_REFRESHED":
              userStore.setIsUserSignedIn(true);
              break;
          }
        });
      };

      let lastValidateTime = 0;
      const validateAndRefreshToken = async () => {
        try {
          // Check if function was called within last 5 seconds
          const now = Date.now();
          if (now - lastValidateTime < 5000) {
            console.log("Skipping token validation - called too soon");
            return;
          }
          lastValidateTime = now;

          // default to minimum 5 days (120 hours)
          let _minTokenAge = 120;
          // see if network token age is set, if not then use default 5 days (120 hours)
          let _netWorkTokenAge = useAppSettingsStore().getAppSettings(
            Constants.enums.APP_SETTINGS.MIN_TOKEN_AGE
          );
          // if network token age is set, then use it, otherwise use default 5 days (120 hours)
          _minTokenAge = _netWorkTokenAge ? _netWorkTokenAge : _minTokenAge;

          // if token is not valid for more than min token age then refresh the token
          if (Helper.getTokenAge() <= _minTokenAge) {
            console.log(
              `Token is getting expires in ${Helper.getTokenAge()} hours, refreshing it...`
            );
            // refresh the token
            Auth.refreshSession();
          } else {
            console.log(
              `Token is valid for more than ${_minTokenAge} hours, will skip the refreshing it`
            );
          }
        } catch (error) {
          Exception.catchException(error);
          errorObj.value.errorMsg = error;
          errorObj.value.headerMsg =
            "Error Occured while validating and refreshing token !!";
          errorObj.value.isVisible = true;
        }
      };

      const initRxDBProcess = async () => {
        try {
          let isAuthenticated = await Auth.isUserSignedIn();
          console.log("isAuthenticated", isAuthenticated);
          if (isAuthenticated) {
            // after pinia store init
            // Simple logic: show dialog only on first sync
            if (userStore.isOnline && !isFirstSyncCompleted.value) {
              // First sync - show dialog
              console.log("First sync detected, showing sync dialog");
              useRxDBStore().resetCollectionSyncStatus();
              offlineDataProgressObj.value.isVisible = true;
              offlineDataProgressObj.value.isLoading = true;
              offlineDataProgressObj.value.headerMsg = "Syncing data...";
            } else {
              // Subsequent syncs or offline - no dialog, seamless experience
              console.log("Subsequent sync or offline mode - no dialog needed");
            }
            await initDatabase("App");
          }
        } catch (error) {
          Exception.catchException(error);
          errorObj.value.errorMsg = error;
          errorObj.value.headerMsg = "Error Occured while initializing RXDB !!";
          errorObj.value.isVisible = true;
        }
      };

      const checkForUpdates = () => {
        console.log("Checking for web updates...");
        serviceWorkerManager.checkForUpdates();
      };

      const periodicCheckForUpdates = () => {
        checkForUpdates();
        setInterval(
          () => {
            checkForUpdates();
          },
          Helper.isCurrentEnvironmentDev() ? 30e3 : 300e3
        ); // 30 sec if dev, 5 mins otherwise
      };

      const listenForServiceWorkerUpdateEvents = () => {
        updateEventBus.on("updateAvailable", (data) => {
          if (data.downloaded) {
            Helper.showToast(
              "Update Downloaded",
              "A new version of app is downloaded in background.",
              "success"
            );
          } else {
            Helper.showToast(
              "Update Available",
              "A new version is being downloaded in background...",
              "info"
            );
          }
        });

        updateEventBus.on("updateApplied", () => {
          console.log("App is ready to reload !! ");
          checkVersion();
        });
        // init periodic check for updates
        periodicCheckForUpdates();
      };

      const onEnvironmentInfoDialogClose = () => {
        try {
          const env = Helper.getCurrentEnvironment();
          const version = Helper.getCurrentVersion();
          const storageKey = `phoenix_env_notice_ack_${env}_${version}`;
          localStorage.setItem(storageKey, "1");
        } catch {
          // ignore storage errors (private mode, restricted env, etc.)
        }
        envInfoObj.value.isVisible = false;
      };

      const showEnvironmentInfoDialogIfNeeded = () => {
        const isDevOrStaging =
          Helper.isCurrentEnvironmentDev() ||
          Helper.isCurrentEnvironmentStaging();
        if (!isDevOrStaging) return;

        const env = Helper.getCurrentEnvironment();
        const version = Helper.getCurrentVersion();
        const storageKey = `phoenix_env_notice_ack_${env}_${version}`;

        try {
          if (localStorage.getItem(storageKey) === "1") return;
        } catch {
          // ignore storage read errors
        }

        const envLabel = (env || "development").toUpperCase();
        envInfoObj.value.headerMsg = `${envLabel} Environment`;
        envInfoObj.value.message = `
        <div>
          <p><strong>Environment notice</strong></p>
          <p>
            You are currently using the
            <strong><span class="text-orange-600">${envLabel}</span></strong>
            environment.
          </p>
          <ul class="list-disc pl-6 mt-3 space-y-2">
            <li>This environment is intended for testing and validation.</li>
            <li>Data saved here may be reset or removed without notice.</li>
            <li>Please avoid entering sensitive or production-only information.</li>
          </ul>
        </div>`;
        envInfoObj.value.isVisible = true;
      };

      onMounted(async () => {
        showEnvironmentInfoDialogIfNeeded();
        // listen for auth change
        listenForAuthChange();
        if (userStore.isUserSignedIn() && userStore.isOnline) {
          validateAndRefreshToken();
          // get roles from the profile
          const userId = userStore.getUserData.id;
          if (userId) {
            const profileStore = useProfileStore();
            const latestProfile = await profileStore.fetchProfile(userId, true);
            if (latestProfile?.data?.roles) {
              userStore.setUserData({ roles: latestProfile.data.roles });
            }
            // Permissions are now initialized in main.js before app render
          }
        }
        await getAllIndexedDBTables();
        await initRxDBProcess();
      });
      // Watch for changes in the route path
      watch(
        () => route.path,
        async () => {
          isVisible.value = await Auth.isUserSignedIn();
        },
        { immediate: true } // This ensures the watcher runs initially as well
      );

      // If user loses permission while staying on a route, redirect to Home.
      // The route guard only runs on navigation; permissions can change live via subscription.
      let permissionDriftTimer = null;
      const enforceCurrentRoutePermission = async () => {
        try {
          if (!route?.meta?.requiresAuth) return;

          // Always allow these routes / avoid loops
          if (
            route.path === Constants.enums.ROUTE_ENUM.HOME ||
            route.path === Constants.enums.ROUTE_ENUM.LOG_IN ||
            route.path === Constants.enums.ROUTE_ENUM.LOG_OUT ||
            route.path === Constants.enums.ROUTE_ENUM.PERMISSION_DENIED ||
            route.path === Constants.enums.ROUTE_ENUM.AUTH_CALLBAK ||
            route.path === Constants.enums.ROUTE_ENUM.AUTH_CALLBAK_MOBILE_APP
          ) {
            return;
          }

          // Only enforce when authenticated (otherwise guard will handle)
          const isAuthenticated = await Auth.isUserSignedIn();
          if (!isAuthenticated) return;

          const permissionCheck = await checkRouteAccess(route);
          if (!permissionCheck.allowed) {
            Helper.showToast(
              "Access Updated",
              "Your permissions changed. Redirecting to Home.",
              "warn"
            );
            router.replace(Constants.enums.ROUTE_ENUM.HOME);
          }
        } catch (e) {
          console.error("Error enforcing current route permission:", e);
        }
      };

      const scheduleEnforce = () => {
        if (permissionDriftTimer) clearTimeout(permissionDriftTimer);
        permissionDriftTimer = setTimeout(() => {
          enforceCurrentRoutePermission();
        }, 0);
      };

      // Re-check access when permissions update live
      watch(
        () => permissionsStore.getPermissions,
        () => scheduleEnforce(),
        { deep: true }
      );
      const onUpdateDialogClose = () => {
        updateObj.value.isVisible = false;
        updateObj.value.errorMsg = "";
        updateObj.value.forceUpdate = false;
        // Reload the page to apply the new update
        window.location.reload();
      };
      const indexedDBTables = ref([]);

      const getAllIndexedDBTables = async () => {
        const tables = await Helper.getAllIndexedDBTables();
        indexedDBTables.value = tables;
      };
      return {
        Helper,
        counter,
        isVisible,
        errorObj,
        envInfoObj,
        isHideHeader,
        Constants,
        updateObj,
        offlineDataProgressObj,
        onUpdateDialogClose,
        onEnvironmentInfoDialogClose,
        isAllCollectionsSynced,
        isAllCollectionsAdded,
        isWaitOver,
        isFirstSyncCompleted,
        isCollectionTableAdded,
        isUserSignedIn,
        getAllIndexedDBTables,
        indexedDBTables
      };
    }
  });
</script>
